import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ConsolBadge from '../components/ConsolBadge';
import {
  Box,
  Container,
  Heading,
  Text,
  Grid,
  Card,
  Badge,
  Button,
  VStack,
  HStack,
  Icon,
  Spinner,
  Flex,
  Dialog,
  IconButton,
  Tabs
} from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../services/dataService';
import { branchDataService } from '../services/branchDataService';
import ViewAgentDetailsModal from '../components/ViewAgentDetailsModal';
import EditAgentModal from '../components/EditAgentModal';
import SendToBlacklistModal from '../components/SendToBlacklistModal';
import { FiX } from 'react-icons/fi';
import { LuContainer, LuPackage, LuPlane, LuWrench, LuCheck, LuX } from 'react-icons/lu';

const PendingQueue = () => {
  const { hasRole, canEditMain, canEditBranch } = useAuth();
  const [pendingAgents, setPendingAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState(() => {
    if (canEditMain()) return 'main';
    if (canEditBranch()) return 'branch';
    return 'main';
  });

  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [specialServicesModalOpen, setSpecialServicesModalOpen] = useState(false);
  const [selectedSpecialServices, setSelectedSpecialServices] = useState([]);
  const [sendToBlacklistModalOpen, setSendToBlacklistModalOpen] = useState(false);
  const [selectedAgentForBlacklist, setSelectedAgentForBlacklist] = useState(null);

  // Tab change handler
  const handleTabChange = (details) => {
    const newTab = details.value;
    setActiveTab(newTab);
    setPendingAgents([]);
    setError(null);
  };

  useEffect(() => {
    if (!canEditMain() && canEditBranch()) {
      setActiveTab('branch');
    }
  }, [canEditMain, canEditBranch]);

  useEffect(() => {
    loadPendingAgents();
  }, [activeTab]);

  const loadPendingAgents = async () => {
    setLoading(true);
    setError(null);
    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      const response = await service.getPendingAgents();
      setPendingAgents(response.data);
    } catch (error) {
      console.error('Error loading pending agents:', error);
      setError('Failed to load pending agents');
    } finally {
      setLoading(false);
    }
  };

  const getServiceAvailability = (serviceValue) => {
    const normalizedValue = serviceValue ? serviceValue.toString().trim().toUpperCase() : '';

    return normalizedValue &&
      normalizedValue !== '' &&
      normalizedValue !== '-' &&
      normalizedValue !== 'NULL' &&
      (
        ['YES', 'AVAILABLE', 'TRUE', 'Y'].includes(normalizedValue) ||
        ['X'].includes(normalizedValue) ||
        normalizedValue.includes('FCL') ||
        normalizedValue.includes('LCL') ||
        normalizedValue.includes('AIR') ||
        normalizedValue.includes('FREIGHT') ||
        normalizedValue.includes('CARGO') ||
        normalizedValue.includes('SEA') ||
        normalizedValue.includes('REEFER') ||
        normalizedValue.includes('WAREHOUSING')
      );
  };

  const getCompactServiceBadge = (serviceName, serviceValue, icon, isStrongest = false) => {
    const isAvailable = getServiceAvailability(serviceValue);
    const colorPalette = isAvailable ? 'green' : 'red';
    const statusIcon = isAvailable ? <Icon as={LuCheck} boxSize="14px" /> : <Icon as={LuX} boxSize="14px" />;

    return (
      <Badge
        colorPalette={colorPalette}
        variant={isAvailable ? 'solid' : 'subtle'}
        border={isAvailable ? undefined : 'none'}
        px={2}
        py={1}
        borderRadius="md"
        fontSize="xs"
        display="inline-flex"
        alignItems="center"
        gap={1}
        title={`${serviceName}: ${isAvailable ? 'Available' : 'Not Available'}${isStrongest ? ' (Strongest Service)' : ''}`}
        boxShadow={isStrongest && isAvailable ? '0 0 10px 3px rgba(234, 179, 8, 0.8), inset 0 0 5px rgba(234, 179, 8, 0.3)' : 'none'}
        outline={isStrongest && isAvailable ? '2px solid' : 'none'}
        outlineColor={isStrongest && isAvailable ? 'yellow.400' : 'transparent'}
        outlineOffset={isStrongest && isAvailable ? '1px' : '0'}
      >
        <Icon as={icon} boxSize="14px" /> {serviceName} {statusIcon}
        {isStrongest && isAvailable && <Text as="span" ml={1}>⭐</Text>}
      </Badge>
    );
  };

  // Handle special services click to show popup
  const handleSpecialServicesClick = (specialServicesString) => {
    if (!specialServicesString || specialServicesString.trim() === '') {
      setSelectedSpecialServices([]);
    } else {
      // Strictly use semicolon delimiter
      const services = specialServicesString.split(';').map(s => s.trim()).filter(s => s);
      setSelectedSpecialServices(services);
    }
    setSpecialServicesModalOpen(true);
  };

  // Get special service badge - clickable badge with popup for pending queue
  const getSpecialServiceBadge = (specialServicesString) => {
    const hasServices = specialServicesString && specialServicesString.trim() !== '';
    let services = [];

    if (hasServices) {
      // Strictly use semicolon delimiter
      services = specialServicesString.split(';').map(s => s.trim()).filter(s => s);
    }

    const colorPalette = hasServices && services.length > 0 ? 'purple' : 'gray';
    const serviceCount = services.length;

    return (
      <Badge
        colorPalette={colorPalette}
        variant="subtle"
        px={2}
        py={1}
        borderRadius="full"
        fontSize="xs"
        cursor="pointer"
        display="inline-flex"
        alignItems="center"
        gap={1}
        title={hasServices ? `Special Services (${serviceCount}): Click to view details` : 'Special Service: Not Available'}
        onClick={() => handleSpecialServicesClick(specialServicesString)}
        _hover={{ opacity: 0.8 }}
      >
        <Icon as={LuWrench} boxSize="14px" /> Special {hasServices ? `(${serviceCount})` : ''}
      </Badge>
    );
  };

  const handleViewDetails = (agent) => {
    setSelectedAgent(agent);
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedAgent(null);
  };

  const handleEditAgent = (agent) => {
    setSelectedAgent(agent);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedAgent(null);
  };

  const handleSaveAgent = (updatedAgent) => {
    setPendingAgents(prev =>
      prev.map(agent =>
        agent.id === updatedAgent.id ? updatedAgent : agent
      )
    );
  };

  const handleRestoreFromQueue = async (agent) => {
    const agentName = agent.agent_name || 'this agent';
    const country = agent.country || 'Unknown Country';

    const confirmRestore = window.confirm(
      `Are you sure you want to restore ${agentName} (${country}) from the pending queue?\n\nThis will make it visible in public views.`
    );

    if (!confirmRestore) {
      return;
    }

    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      await service.restoreFromQueue(agent.id);

      // Remove the agent from pending results
      setPendingAgents(prev =>
        prev.filter(a => a.id !== agent.id)
      );

      alert(`Agent "${agentName}" has been restored from the pending queue.`);
    } catch (error) {
      console.error('Error restoring agent from queue:', error);
      if (error.response?.status === 403) {
        alert('You do not have permission to restore agents from queue. Only editors and admins can do this.');
      } else {
        alert(`Failed to restore agent from queue: ${error.response?.data?.error || error.message || 'Unknown error'}`);
      }
    }
  };

  const handleDeleteAgent = async (agent) => {
    const agentName = agent.agent_name || 'this agent';
    const country = agent.country || 'Unknown Country';

    const confirmDelete = window.confirm(
      `Are you sure you want to DELETE ${agentName} (${country}) permanently?\n\nThis action cannot be undone and will remove the agent completely from the database.`
    );

    if (!confirmDelete) {
      return;
    }

    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      await service.deleteAgent(agent.id);

      // Remove the agent from pending queue
      setPendingAgents(prev =>
        prev.filter(a => a.id !== agent.id)
      );

      alert(`Agent "${agentName}" has been permanently deleted.`);
    } catch (error) {
      console.error('Error deleting agent:', error);
      if (error.response?.status === 403) {
        alert('You do not have permission to delete agents. Only admins can delete agents.');
      } else {
        alert(`Failed to delete agent: ${error.response?.data?.error || error.message || 'Unknown error'}`);
      }
    }
  };

  // Handle send to blacklist - open modal
  const handleSendToBlacklist = (agent) => {
    setSelectedAgentForBlacklist(agent);
    setSendToBlacklistModalOpen(true);
  };

  // Handle confirm send to blacklist
  const handleConfirmSendToBlacklist = async (agentId, sourceTable, reason) => {
    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      await service.moveToBlacklist(agentId, sourceTable, reason);

      // Remove the agent from pending queue
      setPendingAgents(prev => prev.filter(a => a.id !== agentId));

      setSendToBlacklistModalOpen(false);
      setSelectedAgentForBlacklist(null);
      alert(`Agent has been moved to blacklist successfully`);
    } catch (error) {
      console.error('Error moving to blacklist:', error);
      throw error;
    }
  };

  const handleCloseSendToBlacklistModal = () => {
    setSendToBlacklistModalOpen(false);
    setSelectedAgentForBlacklist(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        timeZone: 'Asia/Jakarta',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Container maxW="container.xl">
        <VStack spacing={8} align="stretch">
          <Box textAlign="center" py={4}>
            <Heading as="h1" size="2xl" color="purple.600" mb={3}>PENDING AGENTS</Heading>
            <Text fontSize="xl" color="gray.500">Manage agents in the pending queue</Text>
          </Box>

          {/* Main/Branch Tabs */}
          <Tabs.Root defaultValue="main" value={activeTab} onValueChange={handleTabChange}>
            <Tabs.List mb={4}>
              {canEditMain() && (
                <Tabs.Trigger value="main" px={6} py={2}>
                  <Text fontWeight="medium">Main Queue</Text>
                </Tabs.Trigger>
              )}
              {canEditBranch() && (
                <Tabs.Trigger value="branch" px={6} py={2}>
                  <Text fontWeight="medium">Branch Queue</Text>
                </Tabs.Trigger>
              )}
              <Tabs.Indicator />
            </Tabs.List>
          </Tabs.Root>

          {/* Show error message if error */}
          {error && (
            <Box bg="red.50" color="red.500" p={4} borderRadius="md">
              {error}
            </Box>
          )}

          {/* Show loading spinner */}
          {loading && (
            <Flex justify="center" align="center" py={10}>
              <VStack spacing={4}>
                <Spinner size="xl" color="purple.500" thickness="4px" />
                <Text color="gray.500" fontWeight="medium">Loading pending agents...</Text>
              </VStack>
            </Flex>
          )}

          {/* Show empty state only when no agents and not loading */}
          {!loading && pendingAgents.length === 0 && !error && (
            <Card.Root shadow="lg" borderRadius="xl" borderTop="4px" borderTopColor="purple.500">
              <Card.Body p={8}>
                <Box textAlign="center" py={10} color="gray.500">
                  <Text fontSize="4xl" mb={4}>📭</Text>
                  <Heading size="md" mb={2}>No Pending Agents</Heading>
                  <Text>The pending queue is empty. All agents are currently visible in public views.</Text>
                </Box>
              </Card.Body>
            </Card.Root>
          )}

          {pendingAgents.length > 0 && (
            <Box>
              <Flex justify="space-between" align="center" mb={6}>
                <Heading size="lg" color="purple.700">Pending Agents</Heading>
                <Text color="gray.600">
                  {pendingAgents.length} agent{pendingAgents.length !== 1 ? 's' : ''} in queue
                </Text>
              </Flex>

              <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={8} mb={10}>
                {pendingAgents.map((result, index) => {
                  const strongestServices = result.strongest_service
                    ? result.strongest_service.split(',').map(s => s.trim()).filter(s => s)
                    : [];
                  return (
                    <Card.Root key={index} shadow="md" borderRadius="xl" _hover={{ shadow: 'xl', transform: 'translateY(-2px)' }} transition="all 0.2s" borderTop="4px" borderTopColor="orange.400">
                      <Card.Body>
                        {/* Queue Status Badge */}
                        <Box mb={2}>
                          {result.queue_status ? (
                            <Badge colorPalette={result.queue_status === 'STOP' ? 'red' : 'orange'} mb={2}>
                              {result.queue_status === 'STOP' ? '🛑 STOP' : '⏳ PENDING'}
                            </Badge>
                          ) : (
                            <Badge colorPalette="orange" mb={2}>⏳ PENDING</Badge>
                          )}
                        </Box>

                        {/* Agent Name and Country */}
                        <Flex justify="space-between" align="start" mb={3}>
                          <Box flex={1} minW={0}>
                            <Heading size="md" mb={1} noOfLines={1} title={result.agent_name} color="gray.800">
                              {result.agent_name || 'No Name'}
                            </Heading>
                            <Badge colorPalette="purple" variant="subtle" borderRadius="full" px={2} py={0.5} fontSize="xs">
                              {result.country}
                            </Badge>
                          </Box>
                          {/* Favorite star - visible for all if favorited */}
                          {result.is_favorite && (
                            <Text fontSize="lg" color="yellow.400" ml={2}>⭐</Text>
                          )}
                        </Flex>

                        {/* Service Badges */}
                        {(() => {
                          const services = [
                            { name: 'FCL', value: result.fcl, icon: LuContainer, isStrongest: strongestServices.includes('FCL') },
                            { name: 'LCL', value: result.lcl, icon: LuPackage, isStrongest: strongestServices.includes('LCL') },
                            { name: 'AIR', value: result.air, icon: LuPlane, isStrongest: strongestServices.includes('AIR') }
                          ];

                          const availableServices = services.filter((service) => getServiceAvailability(service.value));
                          const unavailableServices = services.filter((service) => !getServiceAvailability(service.value));

                          return (
                            <VStack align="stretch" spacing={2} mb={4}>
                              <Box>
                                <ConsolBadge
                                  consolExport={result.consol_export}
                                  consolImport={result.consol_import}
                                  consolOversea={result.consol_oversea}
                                />
                              </Box>
                              <HStack wrap="wrap" spacing={2}>
                                {availableServices.map((service) => (
                                  <React.Fragment key={service.name}>
                                    {getCompactServiceBadge(service.name, service.value, service.icon, service.isStrongest)}
                                  </React.Fragment>
                                ))}
                                {getSpecialServiceBadge(result.special_service)}
                              </HStack>
                              {unavailableServices.length > 0 && (
                                <HStack wrap="wrap" spacing={2}>
                                  {unavailableServices.map((service) => (
                                    <React.Fragment key={service.name}>
                                      {getCompactServiceBadge(service.name, service.value, service.icon, service.isStrongest)}
                                    </React.Fragment>
                                  ))}
                                </HStack>
                              )}
                            </VStack>
                          );
                        })()}

                        {/* Action Buttons */}
                        <VStack align="stretch" spacing={2}>
                          <Button size="sm" variant="outline" colorPalette="purple" width="full" onClick={() => handleViewDetails(result)}>
                            SHOW DETAILS
                          </Button>
                          {((activeTab === 'main' && canEditMain()) || (activeTab === 'branch' && canEditBranch())) && (
                            <HStack>
                              <Button size="sm" flex={1} variant="outline" colorPalette="purple" onClick={() => handleEditAgent(result)}>EDIT</Button>
                              <Button size="sm" flex={1} variant="outline" colorPalette="green" onClick={() => handleRestoreFromQueue(result)}>RESTORE</Button>
                            </HStack>
                          )}
                          {((activeTab === 'main' && canEditMain()) || (activeTab === 'branch' && canEditBranch())) && (
                            <HStack>
                              <Button size="sm" flex={1} variant="outline" colorPalette="red" onClick={() => handleSendToBlacklist(result)}>🚫 BLACKLIST</Button>
                              {hasRole(['Admin']) && (
                                <Button size="sm" flex={1} colorPalette="red" variant="solid" onClick={() => handleDeleteAgent(result)}>DELETE</Button>
                              )}
                            </HStack>
                          )}
                        </VStack>
                      </Card.Body>
                    </Card.Root>
                  );
                })}
              </Grid>
            </Box>
          )}
        </VStack>
      </Container>

      {/* View Agent Details Modal */}
      {viewModalOpen && selectedAgent && (
        <ViewAgentDetailsModal
          agent={selectedAgent}
          onClose={handleCloseViewModal}
          formatDate={formatDate}
          dataType={activeTab}
        />
      )}

      {/* Edit Agent Modal */}
      {editModalOpen && selectedAgent && (
        <EditAgentModal
          agent={selectedAgent}
          onClose={handleCloseEditModal}
          onSave={handleSaveAgent}
          dataType={activeTab}
        />
      )}

      {/* Send to Blacklist Modal */}
      {sendToBlacklistModalOpen && selectedAgentForBlacklist && (
        <SendToBlacklistModal
          agent={selectedAgentForBlacklist}
          sourceTable={activeTab === 'branch' ? 'pending_queue_branch' : 'pending_queue'}
          onClose={handleCloseSendToBlacklistModal}
          onConfirm={handleConfirmSendToBlacklist}
        />
      )}

      {/* Special Services Modal */}
      {/* Special Services Modal */}
      {specialServicesModalOpen && createPortal(
        <Dialog.Root open={true} onOpenChange={(e) => !e.open && setSpecialServicesModalOpen(false)}>
          <Dialog.Backdrop bg="blackAlpha.600" backdropFilter="blur(4px)" zIndex={9999} />
          <Dialog.Positioner zIndex={10000}>
            <Dialog.Content maxW="md" borderRadius="xl" boxShadow="2xl" bg="white" p={6}>
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="md" color="purple.700" fontWeight="bold">Special Services</Heading>
                <IconButton
                  size="sm"
                  variant="ghost"
                  onClick={() => setSpecialServicesModalOpen(false)}
                  aria-label="Close"
                >
                  <FiX />
                </IconButton>
              </Flex>
              <VStack align="stretch" spacing={2} maxH="60vh" overflowY="auto">
                {selectedSpecialServices.length === 0 ? <Text color="gray.500">No special services</Text> :
                  selectedSpecialServices.map((s, i) => (
                    <HStack key={i} p={3} bg="purple.50" borderRadius="lg" border="1px solid" borderColor="purple.100" justify="space-between">
                      <HStack>
                        <Badge colorScheme="purple" borderRadius="full" boxSize="24px" display="flex" alignItems="center" justifyContent="center">
                          {i + 1}
                        </Badge>
                        <Text fontWeight="medium" color="gray.700">{s}</Text>
                      </HStack>
                      <Box as="svg" viewBox="0 0 20 20" fill="green" boxSize={5}>
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </Box>
                    </HStack>
                  ))
                }
              </VStack>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>,
        document.body
      )}
    </Box>
  );
};

export default PendingQueue;
